import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { PublicLayout } from './BookPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, Send, CheckCircle2, MessageCircleHeart } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };
const SUBJECTS = ['livre', 'masterclass', 'partenariat', 'presse', 'autre'];

export default function ContactPage() {
  const { t } = useI18n();
  const [form, setForm] = useState({ nom: '', email: '', telephone: '', sujet: '', message: '' });
  const [hp, setHp] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [ts] = useState(() => Date.now() / 1000);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get('/settings').then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.includes('@') || form.telephone.replace(/\D/g, '').length < 8 || !form.sujet || !form.message.trim()) return;
    setSending(true);
    try {
      await api.post('/contact', { ...form, website: hp, ts });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('contact.error'));
    } finally {
      setSending(false);
    }
  };

  const pill = (active) => `px-4 py-2 rounded-full border-2 text-sm font-bold transition-colors ${active ? 'bg-or-400 border-or-400 text-nuit-950' : 'border-nuit-600 text-brume hover:border-or-400/50 hover:text-creme'}`;

  return (
    <PublicLayout testid="contact-page" pageKey="page_contact">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase flex items-center gap-2">
          <MessageCircleHeart className="w-4 h-4" strokeWidth={2.5} /> {t('contact.eyebrow')}
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-3" data-testid="contact-title">
          {t('contact.title')}
        </h1>
        <p className="text-base sm:text-lg text-brume leading-relaxed mt-6 max-w-2xl">{t('contact.intro')}</p>
      </motion.div>

      <div className="grid lg:grid-cols-5 gap-8 mt-10 items-start">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.1 }}
          className="lg:col-span-2 bg-nuit-800 border border-or-400/30 rounded-3xl p-8 gold-glow"
          data-testid="contact-direct">
          <h2 className="text-xl font-bold text-creme font-fredoka mb-4">{t('contact.directTitle')}</h2>
          <p className="text-base text-brume leading-relaxed mb-6">{settings?.content_contact_reply48 || t('contact.reply48')}</p>
          <div className="space-y-3">
            <a href="mailto:dimitri.mfoumou@gmail.com" data-testid="contact-email-link"
              className="flex items-center gap-3 rounded-2xl border border-nuit-600 bg-nuit-900 px-4 py-3 text-sm font-bold text-creme hover:border-or-400/50 hover:text-or-300 transition-colors">
              <Mail className="w-4 h-4 text-or-400 shrink-0" strokeWidth={2.5} /> dimitri.mfoumou@gmail.com
            </a>
            <a href="tel:+33698697558" data-testid="contact-tel-link"
              className="flex items-center gap-3 rounded-2xl border border-nuit-600 bg-nuit-900 px-4 py-3 text-sm font-bold text-creme hover:border-or-400/50 hover:text-or-300 transition-colors">
              <Phone className="w-4 h-4 text-or-400 shrink-0" strokeWidth={2.5} /> +33 6 98 69 75 58
            </a>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.15 }}
          className="lg:col-span-3 bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-8">
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 className="w-14 h-14 text-or-400 mx-auto mb-4" strokeWidth={2.5} />
              <p className="text-2xl font-bold text-creme font-fredoka">{t('contact.success')}</p>
              <p className="text-base text-brume mt-3 max-w-md mx-auto">{t('contact.successSub')}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form">
              <input type="text" name="website" value={hp} onChange={(e) => setHp(e.target.value)}
                tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="contact-honeypot"
                className="hidden" />
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <Input data-testid="contact-name" value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder={t('contact.name')}
                  className="h-12 rounded-2xl text-base font-bold bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
                <Input type="email" data-testid="contact-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('contact.email')}
                  className="h-12 rounded-2xl text-base font-bold bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
              </div>
              <Input type="tel" data-testid="contact-phone" value={form.telephone}
                onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder={t('contact.phone')}
                className="h-12 rounded-2xl text-base font-bold bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40 mb-4" />
              <p className="text-xs font-bold text-brume uppercase tracking-widest mb-2">{t('contact.subjectLabel')}</p>
              <div className="flex flex-wrap gap-2 mb-4" data-testid="contact-subjects">
                {SUBJECTS.map((s) => (
                  <button type="button" key={s} onClick={() => setForm({ ...form, sujet: s })}
                    data-testid={`contact-subject-${s}`} className={pill(form.sujet === s)}>
                    {t(`contact.subject.${s}`)}
                  </button>
                ))}
              </div>
              <Textarea data-testid="contact-message" value={form.message} rows={6}
                onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t('contact.message')}
                className="rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40 mb-5" />
              <Button type="submit" data-testid="contact-submit"
                disabled={sending || !form.nom.trim() || !form.email.includes('@') || form.telephone.replace(/\D/g, '').length < 8 || !form.sujet || !form.message.trim()}
                className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform gap-2">
                {sending ? t('landing.checking') : t('contact.submit')}
                <Send className="w-5 h-5" strokeWidth={2.5} />
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </PublicLayout>
  );
}
